import { useMemo } from 'react';
import {
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * `useMemberships()` (W102-F1-S1) — read the current user's entity-scoped
 * memberships from Senviro-api's `GET /api/v1/auth/me/memberships`.
 *
 * Drives the multi-org switcher in the topbar UserMenu (W102-F1-S2). The
 * backend handler reads off `Principal.memberships`, which was populated
 * by W100-F2-S1's AuthGuard fetch of Core `/users/me` at JWT-verify time.
 * No round-trip to Core happens for this call — Senviro-api just reads
 * already-cached principal state.
 *
 * Shape matches `MembershipDto` from `@senviro/shared` (snake_case fields
 * mirroring Core's wire format). Cached for the session by TanStack
 * Query with a 5-minute stale time — memberships are low-churn but
 * security-relevant; longer caching risks showing a switcher entry
 * for an org the user just lost access to.
 */

/**
 * One entity-scoped membership row. Mirrors Core's `MembershipRead` shape
 * + the Zod schema in `packages/shared/src/memberships.ts`. We duplicate
 * the type locally rather than importing from `@senviro/shared` to keep
 * the SPA bundle independent (cleanly tree-shakes the shared package's
 * server-only modules).
 */
export interface Membership {
  entity_type: 'building' | 'building_operational' | 'partner_relationship';
  entity_id: string;
  organization_id: string;
  role: string;
  source:
    | 'user_building_access'
    | 'building_operational_roles'
    | 'partner_relationships';
  effective_from: string;
  effective_until: string | null;
}

export interface MembershipsResponse {
  homeOrgId: string | undefined;
  memberships: Membership[];
}

const MEMBERSHIPS_KEY = ['memberships', 'me'] as const;

/**
 * Raw query — returns the full `{ homeOrgId, memberships }` response.
 * Most consumers want `useAvailableOrgIds()` or
 * `useMembershipsReady()` instead.
 */
export function useMembershipsQuery(): UseQueryResult<MembershipsResponse> {
  const { isAuthenticated, getAccessToken } = useAuth();
  return useQuery({
    queryKey: MEMBERSHIPS_KEY,
    queryFn: async () =>
      apiFetch<MembershipsResponse>('/api/v1/auth/me/memberships', {
        getAccessToken,
      }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Derived list of `organization_id` values the user can be active in —
 * union of `homeOrgId + distinct(memberships.organization_id)`. The
 * W100-F2-S2 `resolveActiveOrg` helper validates `X-Org-Id` against
 * this same set on the backend, so the SPA's switcher MUST surface
 * only ids from this list.
 *
 * Returns the empty array when the query hasn't loaded yet (or on
 * dev-bypass / tenant-portal paths where the backend returns empty).
 */
export function useAvailableOrgIds(): string[] {
  const { data } = useMembershipsQuery();
  return useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    if (data.homeOrgId) set.add(data.homeOrgId);
    for (const m of data.memberships) {
      set.add(m.organization_id);
    }
    return Array.from(set);
  }, [data]);
}

/**
 * True iff the user has access to more than one org — drives whether
 * the topbar switcher is rendered at all (W102-F1-S2 hides it for
 * single-org users to avoid UI noise).
 */
export function useIsMultiOrg(): boolean {
  return useAvailableOrgIds().length > 1;
}

/**
 * The user's home org (or undefined while loading / on dev-bypass).
 * Surface this in the switcher as the default selection when the user
 * hasn't explicitly picked another org via the UI.
 */
export function useHomeOrgId(): string | undefined {
  const { data } = useMembershipsQuery();
  return data?.homeOrgId;
}
